import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useSEO } from '../../hooks/useSEO';
import { buildBreadcrumbJsonLd } from '../../config/seo';
import { useMarkdownContent } from '../../hooks/useMarkdownContent';
import { parseFrontmatter } from '../../utils/frontmatter';
import SectionHeading from '../../components/SectionHeading/SectionHeading';
import TeamMemberCard from '../../components/TeamMemberCard/TeamMemberCard';
import BlobDecoration from '../../components/BlobDecoration/BlobDecoration';
import { teamMembers } from '../../data/teamMembers';
import { Language } from '../../routes/routeConfig';
import styles from './About.module.css';

const About: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { content: markdownRaw, loading } = useMarkdownContent('about');

  const { metadata, content } = useMemo(() => {
    if (!markdownRaw) return { metadata: {}, content: '' };
    return parseFrontmatter(markdownRaw);
  }, [markdownRaw]);

  const markdownComponents = useMemo(() => ({
    p: ({ children }: any) => <p className={styles.storyParagraph}>{children}</p>,
    a: ({ href, children }: any) => {
      if (href?.startsWith('/')) {
        return <Link to={href} className={styles.storyLink}>{children}</Link>;
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" className={styles.storyLink}>{children}</a>;
    },
  }), []);

  const seo = useSEO({
    titleKey: 'seo.about.title',
    descriptionKey: 'seo.about.description',
    routeKey: 'about',
    structuredData: buildBreadcrumbJsonLd(
      [
        { name: t('nav.home'), routeKey: 'home' },
        { name: t('nav.about'), routeKey: 'about' },
      ],
      i18n.language as Language
    ),
  });

  return (
    <div className={styles.about}>
      {seo}
      <section className={styles.header}>
        <BlobDecoration color="red" position="top-right" size="md" />
        <BlobDecoration color="yellow" position="bottom-left" size="sm" />
        <div className="container">
          <h1 className={styles.pageTitle}>{t('about.pageTitle')}</h1>
        </div>
      </section>

      <section className={styles.storySection}>
        <div className="container">
          <SectionHeading
            title={metadata.storyTitle || t('about.storyTitle')}
            accentColor="var(--color-blue)"
          />
          <div className={styles.storyContent}>
            {loading ? null : content ? (
              <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
            ) : (
              t('about.storyText')
                .split('\n\n')
                .map((paragraph: string, index: number) => (
                  <p key={index} className={styles.storyParagraph}>
                    {paragraph}
                  </p>
                ))
            )}
          </div>
        </div>
      </section>

      <section className={styles.teamSection}>
        <div className="container">
          <SectionHeading
            title={metadata.teamTitle || t('about.teamTitle')}
            accentColor="var(--color-yellow)"
          />
          <div className={styles.teamGrid}>
            {teamMembers.map((member) => (
              <TeamMemberCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
